import React, { useState } from 'react';
import { Check, ShieldCheck, Lock, Quote, Star, ChevronDown } from 'lucide-react';
import { resolveMediaUrl } from '../../lib/api';

/** Phones get a tight checklist (titles only); the explanatory bodies show from `sm` up. */
export function IncludedList({ cfg }) {
  const items = (cfg.included || []).filter((i) => i && (i.title || i.body));
  if (!items.length) return null;
  return (
    <section className="px-1" data-testid="included-section">
      <h2 className="font-serif text-xl sm:text-2xl text-slate-900 mb-2.5 sm:mb-4">{cfg.included_title || "What's included"}</h2>
      <ul className="space-y-2 sm:space-y-4">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2.5 sm:gap-3.5" data-testid={`included-item-${i}`}>
            <span className="mt-0.5 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-slate-900 text-amber-400 flex items-center justify-center shrink-0"><Check size={13} strokeWidth={3} /></span>
            <div>
              <p className="font-semibold text-slate-900 leading-snug text-sm sm:text-base">{it.title}</p>
              {it.body && <p className="hidden sm:block text-sm text-slate-500 mt-0.5 leading-relaxed">{it.body}</p>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function GuaranteeCard({ cfg }) {
  if (!cfg.guarantee_title && !cfg.guarantee_text) return null;
  return (
    <section className="rounded-2xl sm:rounded-3xl border border-emerald-200 bg-emerald-50/60 p-4 sm:p-6 flex gap-3 sm:gap-4 items-start" data-testid="guarantee-card">
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-sm"><ShieldCheck size={22} /></div>
      <div>
        <p className="font-extrabold text-emerald-900 text-sm sm:text-base" data-testid="guarantee-title">{cfg.guarantee_title}</p>
        {cfg.guarantee_text && <p className="text-xs sm:text-sm text-emerald-800/80 mt-0.5 sm:mt-1 leading-relaxed" data-testid="guarantee-text">{cfg.guarantee_text}</p>}
      </div>
    </section>
  );
}

export function TrustRow({ cfg }) {
  const items = (cfg.trust_items || []).filter(Boolean);
  if (!items.length) return null;
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1.5 justify-center" data-testid="trust-row">
      {items.map((t, i) => (
        <li key={i} className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
          <Lock size={11} className="text-slate-400" />{t}
        </li>
      ))}
    </ul>
  );
}

/** Collapsed behind a "Read reviews" toggle on phones; always expanded from `sm` up. */
export function TestimonialsBlock({ cfg }) {
  const [open, setOpen] = useState(false);
  const items = (cfg.testimonials || []).filter((t) => t && (t.quote || t.image_url));
  if (!items.length) return null;
  return (
    <section className="px-1" data-testid="testimonials-section">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="sm:hidden w-full flex items-center justify-between rounded-2xl bg-white border border-slate-200/80 px-4 py-3 text-left" data-testid="testimonials-toggle">
        <span className="flex items-center gap-2">
          <span className="flex items-center">{[0, 1, 2, 3, 4].map((i) => <Star key={i} size={13} className="fill-amber-400 text-amber-400" />)}</span>
          <span className="text-sm font-semibold text-slate-900">{open ? (cfg.label_reviews_hide || 'Hide student reviews') : (cfg.label_reviews_read || 'Read student reviews')} ({items.length})</span>
        </span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`${open ? 'block mt-3' : 'hidden'} sm:block sm:mt-0`} data-testid="testimonials-body">
        <div className="hidden sm:flex items-center gap-1 mb-3">
          {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} className="fill-amber-400 text-amber-400" />)}
        </div>
        {cfg.testimonials_title && <h2 className="hidden sm:block font-serif text-2xl text-slate-900 mb-4">{cfg.testimonials_title}</h2>}
        <div className="space-y-3 sm:space-y-5">
          {items.map((t, i) => (
            <figure key={t.id || i} className="rounded-2xl sm:rounded-3xl bg-white border border-slate-200/80 p-4 sm:p-6" data-testid={`testimonial-${i}`}>
              {t.type === 'image' && t.image_url ? (
                <img src={resolveMediaUrl(t.image_url)} alt={`${t.name} result`} className="w-full rounded-2xl border border-slate-100 mb-4" loading="lazy" />
              ) : (
                <Quote size={20} className="text-amber-500 mb-3" />
              )}
              {t.quote && <blockquote className="text-sm sm:text-base text-slate-800 leading-relaxed">{t.quote}</blockquote>}
              <figcaption className="mt-3 sm:mt-4 flex items-center gap-3">
                <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-slate-900 text-white font-bold flex items-center justify-center text-sm">{(t.name || '?').trim().charAt(0).toUpperCase()}</span>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
                  {t.result && <p className="text-xs font-bold text-emerald-700">{t.result}</p>}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
